// Variables → the respondent data view (#1887). Since 20 Oct (Adam and Matt)
// it is what Variables opens on; the construct tools wait on a hidden page
// (variables-tools) until their UI pass (#1889). Open to whoever may use the
// agent (useDataViewAccess; the server's RequireAIAccessLevel). The level
// lives in /me, not the login token, so the route lets every signed-in user
// through and this page shows anyone below agent access the reason in place
// once /me has answered: there is no page to send them back to. "Opened by"
// is Manager only.
import React, { useState } from 'react'
import { Alert, Box, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import HistoryIcon from '@mui/icons-material/History'
import FilterListIcon from '@mui/icons-material/FilterList'
import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import useVariableTree from '../hooks/useVariableTree'
import useCaseDataView from '../hooks/useCaseDataView'
import useDataViewAccess from '../hooks/useDataViewAccess'
import Tree from '../components/tree/Tree'
import SideBarPage from '../components/system/SidebarPage'
import PageHeader from '../components/system/PageHeader'
import ActionButton from '../components/system/ActionButton'
import CaseDataGrid from '../components/variables-data/CaseDataGrid'
import CaseDataAccessDialog from '../components/variables-data/CaseDataAccessDialog'
import FilterEditorModal from '../components/spec-editor/FilterEditorModal'
import QuickFilterChip from '../components/platinum-table-new/QuickFilterChip'
import { GenNode } from '../types/carbon/models/GenNode'

const PAGE_TITLE = 'Data view'
const PAGE_SUBTITLE = 'One row per respondent, in data order. Open to everyone who can use the agent.'

export const VariablesData: React.FC = () => {
  const router = useRouter()
  const customer = router.params.customer || ''
  const job = router.params.job || ''
  const access = useDataViewAccess(customer)

  // Nothing asks for rows until /me says yes.
  if (!access.decided) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
  }
  if (!access.allowed) {
    return (
      <Box sx={{ p: 2 }}>
        <PageHeader title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />
        <Alert severity="info" sx={{ m: 1 }}>{access.reason}</Alert>
      </Box>
    )
  }
  return <DataViewPage customer={customer} job={job} />
}

const DataViewPage: React.FC<{ customer: string; job: string }> = ({ customer, job }) => {
  const account = useAccount()
  const isManager = account.user?.role_permissions?.isManager || false

  const variableTree = useVariableTree({ customer, job, loadCodes: false, driver: 'carbon' })
  const view = useCaseDataView({ customer, job })
  const [selectedId, setSelectedId] = useState('')

  // Clicking a variable pulls it into the grid as the next column.
  const onNodeSelectToggle = (itemId: string, node: GenNode, isSelected: boolean) => {
    if (!isSelected) return
    setSelectedId(itemId)
    if (node.type === 'Variable' && node.value1) view.handleAddColumn(node.value1)
  }

  const sidebar = variableTree.isLoading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
  ) : account.job && variableTree.nodes && variableTree.nodes.length > 0 ? (
    <Tree
      searchEnabled
      searchPlaceholder="Search variables..."
      showTooltips
      nodes={variableTree.nodes}
      expanded={variableTree.expanded}
      selected={selectedId}
      onNodeExpandToggle={variableTree.onNodeExpandToggle}
      onNodeSelectToggle={onNodeSelectToggle}
    />
  ) : null

  const hasColumns = view.columns.length > 0

  return (
    <>
      <SideBarPage title="Variable Tree" sidebar={sidebar}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <PageHeader
            title={PAGE_TITLE}
            subtitle={PAGE_SUBTITLE}
            actions={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {/* The Tables quick filter (Adam and Matt, 20 Oct): the
                    expression shows as a chip; a click reopens it, X clears it. */}
                <ActionButton icon={<FilterListIcon />} label="Quick filter" onClick={view.handleOpenFilter} />
                {view.filter && <QuickFilterChip filter={view.filter} onClear={view.handleClearFilter} onClick={view.handleOpenFilter} />}
                <FormControlLabel
                  sx={{ mr: 1 }}
                  control={<Switch size="small" color="info" checked={view.showCodes} onChange={(e) => view.handleShowCodes(e.target.checked)} />}
                  label={<Typography variant="body2">Show codes</Typography>}
                />
                <ActionButton
                  icon={view.exporting ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon />}
                  label="Export CSV"
                  onClick={view.handleExportCsv}
                  disabled={!hasColumns || view.exporting}
                />
                <ActionButton icon={<AutoAwesomeOutlinedIcon />} label="Send to agent" onClick={view.handleSendToAgent} disabled={!hasColumns} />
                {/* Who opened the view lists other people's requests: Managers only. */}
                {isManager && <ActionButton icon={<HistoryIcon />} label="Opened by" onClick={view.handleOpenAccess} />}
              </Box>
            }
          />
          {/* Matt, 20 Oct (#1887): the same question can sit in different
              variables from job to job, and a table's sample comes from its filter. */}
          <Alert severity="warning" sx={{ mx: 1, mb: 1 }}>
            Caution: raw data can be structured in different ways in each Job. Please view Tables to see filters applied to achieve samples.
          </Alert>
          {view.exportError && (
            <Alert severity="error" onClose={view.handleDismissExportError} sx={{ m: 1 }}>
              The CSV export failed: {view.exportError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
            {view.error && <Alert severity="error" sx={{ m: 1 }}>{view.error}</Alert>}
            {!hasColumns && (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Click a variable in the tree to pull it into the view. The case column comes first: the job’s case variable, or the row number.
                </Typography>
              </Box>
            )}
            {hasColumns && !view.page && view.isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
            )}
            {view.page && (
              <CaseDataGrid
                page={view.page}
                showCodes={view.showCodes}
                isLoading={view.isLoading}
                limit={view.limit}
                onRemoveColumn={view.handleRemoveColumn}
                onPage={view.handlePage}
                onLimit={view.handleLimit}
              />
            )}
          </Box>
        </Box>
      </SideBarPage>
      <FilterEditorModal
        open={view.filterOpen}
        onClose={view.handleCloseFilter}
        onSave={view.handleSaveFilter}
        currentFilter={view.filter}
        customer={customer}
        job={job}
        hideSourceSelector
        validate={view.validateFilter}
      />
      <CaseDataAccessDialog
        open={view.accessOpen}
        accesses={view.accesses}
        loading={view.accessLoading}
        error={view.accessError}
        onClose={view.handleCloseAccess}
      />
    </>
  )
}

export default VariablesData
